/**
 * DraftSpecDriftPanel — Read-only inline summary of draft↔spec drift,
 * shown inside an expanded Ordering Queue row before Push.
 *
 * Posture: visibility only. Never blocks Push, never mutates anything.
 * Mirrors the bannerFor() pattern from JobBomPanel for visual consistency.
 */

import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CheckCircle2, AlertTriangle, ChevronDown, ChevronRight, Info } from "lucide-react";
import {
  compareDraftToSpec,
  labelForDriftKind,
  type DraftDriftRow,
} from "@/data/orderDraft/draftSpecDrift";
import type { OrderDraft } from "@/data/orderDraft";
import type { Job } from "@/data/types";

interface Props {
  job: Pick<Job, "boundJobSpec">;
  draft: OrderDraft | undefined;
}

export function DraftSpecDriftPanel({ job, draft }: Props) {
  const [open, setOpen] = useState(false);
  const report = compareDraftToSpec(job, draft);

  // No spec bound — neutral empty state.
  if (report.noSpec) {
    return (
      <div className="rounded-md border border-border bg-muted/20 px-3 py-2 text-[11px] text-muted-foreground flex items-center gap-2">
        <Info className="h-3.5 w-3.5 shrink-0" />
        No JobSpec bound — spec alignment can't be checked before push.
      </div>
    );
  }

  // No draft yet — nothing to compare.
  if (!draft) return null;

  const aligned = report.counts.total === 0;
  const totalLabel =
    report.counts.total === 1 ? "1 item differs from spec" : `${report.counts.total} items differ from spec`;

  return (
    <div
      className={`rounded-md border ${
        aligned ? "border-success/30 bg-success/5" : "border-warning/40 bg-warning/[0.04]"
      }`}
    >
      <div className="flex items-center justify-between gap-2 px-3 py-2">
        <div className="flex items-center gap-2 text-[11px]">
          {aligned ? (
            <>
              <CheckCircle2 className="h-3.5 w-3.5 text-success shrink-0" />
              <span className="text-success font-medium">
                Aligned with spec · {draft.vendorGroups.reduce((s, vg) => s + vg.items.length, 0)} line
                {draft.vendorGroups.reduce((s, vg) => s + vg.items.length, 0) === 1 ? "" : "s"} match
              </span>
            </>
          ) : (
            <>
              <AlertTriangle className="h-3.5 w-3.5 text-warning shrink-0" />
              <span className="text-warning font-medium">{totalLabel}</span>
              <span className="text-muted-foreground hidden sm:inline">
                · {report.counts.missingFromOrder} missing · {report.counts.qtyMismatch} qty · {report.counts.notOnSpec} extra
              </span>
            </>
          )}
          {report.specChangedSincePrep && (
            <Badge variant="outline" className="text-[9px] border-border text-muted-foreground gap-1">
              <Info className="h-2.5 w-2.5" />
              Spec changed since draft was prepared
            </Badge>
          )}
        </div>
        {!aligned && (
          <Button
            size="sm"
            variant="ghost"
            className="h-6 text-[10px] gap-1"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            {open ? "Hide" : "Review"}
          </Button>
        )}
      </div>

      {open && !aligned && (
        <div className="border-t px-3 py-2">
          <table className="w-full text-[11px]">
            <thead>
              <tr className="text-left text-muted-foreground">
                <th className="font-medium pb-1 pr-2">SKU</th>
                <th className="font-medium pb-1 pr-2">Item</th>
                <th className="font-medium pb-1 pr-2">Drift</th>
                <th className="font-medium pb-1 pr-2 text-right">Spec qty</th>
                <th className="font-medium pb-1 text-right">Draft qty</th>
              </tr>
            </thead>
            <tbody>
              {report.rows.map((r) => (
                <DriftRow key={`${r.kind}-${r.sku}`} row={r} />
              ))}
            </tbody>
          </table>
          <p className="text-[10px] text-muted-foreground mt-2">
            Read-only. Push is not blocked — review and adjust the draft (or the spec via Change
            Order) if these differences aren't intentional.
          </p>
        </div>
      )}
    </div>
  );
}

function DriftRow({ row }: { row: DraftDriftRow }) {
  const tone =
    row.kind === "missing_from_order"
      ? "text-warning"
      : row.kind === "qty_mismatch"
        ? "text-warning"
        : "text-muted-foreground";
  return (
    <tr className="border-t border-border/50">
      <td className="font-mono text-[10px] py-1 pr-2">{row.sku}</td>
      <td className="py-1 pr-2">{row.productName}</td>
      <td className={`py-1 pr-2 ${tone}`}>{labelForDriftKind(row.kind)}</td>
      <td className="py-1 pr-2 text-right tabular-nums">{row.specQty ?? "—"}</td>
      <td className="py-1 text-right tabular-nums">{row.draftQty ?? "—"}</td>
    </tr>
  );
}

export default DraftSpecDriftPanel;
